import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconName } from '../../icons/icon.component';
import { ThemeService } from '../../../core/services/theme.service';

export type ToasterType = 'success' | 'error' | 'warning' | 'info';
export type ToasterPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToasterAction {
  label: string;
  onClick: () => void;
}

export interface ToasterConfig {
  id: string;
  type: ToasterType;
  title?: string;
  message: string;
  duration?: number;
  position?: ToasterPosition;
  closable?: boolean;
  action?: ToasterAction;
}

@Component({
  selector: 'app-individual-toaster',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './loader.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
      }

      .toaster-progress-bar {
        animation: progress linear forwards;
      }

      @keyframes progress {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }


.loader {
  // color: #ffffff;
  // font-size: 30px;
  text-indent: -9999em;
  overflow: hidden;
  width: 1em;
  height: 1em;
  margin-right: 10px;
  border-radius: 50%;
  position: relative;
  // transform: translateZ(0);
  animation: mltShdSpin 1.7s infinite ease, round 1.7s infinite ease;
}

@keyframes mltShdSpin {
  0% {
    box-shadow: 0 -0.83em 0 -0.4em,
    0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em,
    0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  5%,
  95% {
    box-shadow: 0 -0.83em 0 -0.4em, 
    0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 
    0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  10%,
  59% {
    box-shadow: 0 -0.83em 0 -0.4em, 
    -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, 
    -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em;
  }
  20% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em,
     -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, 
     -0.749em -0.34em 0 -0.477em;
  }
  38% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em,
     -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, 
     -0.82em -0.09em 0 -0.477em;
  }
  100% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 
    0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
}

@keyframes round {
  0% { transform: rotate(0deg) }
  100% { transform: rotate(360deg) }
}
 


    `,
  ],
})
export class IndividualToasterComponent implements OnInit, OnDestroy {
  @Input() id: string = Math.random().toString(36).substring(2);
  @Input() type: ToasterType = 'info';
  @Input() title?: string;
  @Input() message: string = '';
  @Input() duration: number = 5000;
  @Input() position: ToasterPosition = 'bottom-right';
  @Input() closable: boolean = true;
  @Input() action?: ToasterAction;
  @Input() fontSize: string = '30px';
  @Input() loaderType: 'light' | 'dark' = 'light';
  /** Optional CSS color value for the loader backdrop (e.g. '#1C1C1F'). When omitted, getBgColor() Tailwind classes are used. */
  @Input() bg: string | null = null;
  @Output() remove = new EventEmitter<string>();

  isVisible = false;
  isExiting = false;

  private readonly exitAnimationDurationMs = 350;

  private visibilityTimer?: ReturnType<typeof setTimeout>;
  private durationTimer?: ReturnType<typeof setTimeout>;

  private readonly icons: Record<ToasterType, IconName> = {
    success: 'check',
    error: 'x',
    warning: 'triangleAlert',
    info: 'info',
  };

  private readonly typeStyles: Record<ToasterType, string> = {
    success:
      'border-l-emerald-500 bg-gradient-to-r from-emerald-50 to-white dark:border-l-emerald-600 dark:from-emerald-950 dark:to-gray-800',
    error:
      'border-l-red-500 bg-gradient-to-r from-red-50 to-white dark:border-l-red-600 dark:from-red-950 dark:to-gray-800',
    warning:
      'border-l-amber-500 bg-gradient-to-r from-amber-50 to-white dark:border-l-amber-600 dark:from-amber-950 dark:to-gray-800',
    info: 'border-l-blue-500 bg-gradient-to-r from-blue-50 to-white dark:border-l-blue-600 dark:from-blue-950 dark:to-gray-800',
  };

  private readonly iconColors: Record<ToasterType, string> = {
    success: 'text-emerald-500',
    error: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
  };

  private readonly iconBackgroundColors: Record<ToasterType, string> = {
    success: 'bg-emerald-100 dark:bg-emerald-900',
    error: 'bg-red-100 dark:bg-red-900',
    warning: 'bg-amber-100 dark:bg-amber-900',
    info: 'bg-blue-100 dark:bg-blue-900',
  };

  protected readonly progressColors: Record<ToasterType, string> = {
    success: 'bg-emerald-500',
    error: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  ngOnInit(): void {
    this.visibilityTimer = setTimeout(() => {
      this.isVisible = true;
    }, 10);

    if (this.duration && this.duration > 0) {
      this.durationTimer = setTimeout(() => {
        this.handleClose();
      }, this.duration);
    }
  }

  ngOnDestroy(): void {
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
    }
    if (this.durationTimer) {
      clearTimeout(this.durationTimer);
    }
  }

  getIconName(): IconName {
    return this.icons[this.type];
  }

  getIconColorClass(): string {
    return this.iconColors[this.type];
  }

  getIconBackgroundColorClass(): string {
    return this.iconBackgroundColors[this.type];
  }

  getContainerClasses(): string {
    const baseClasses = `relative flex items-start gap-3 p-4 rounded-lg w-full h-full mb-3 shadow-xl 
                         transition-all duration-300 ease-in-out overflow-hidden 
                         bg-white dark:bg-gray-800 dark:shadow-2xl`;

    const typeClasses = this.typeStyles[this.type];

    // let animationClasses = 'opacity-0 translate-y-5';
    // if (this.isVisible) {
    let  animationClasses = 'opacity-100 translate-y-0';
    // }
    // if (this.isExiting) {
    //   animationClasses = 'opacity-0 translate-x-full duration-300 ease-in';
    // }

    return `${baseClasses} ${typeClasses} ${animationClasses}`;
  }

  handleClose(): void {
    if (this.durationTimer) {
      clearTimeout(this.durationTimer);
    }

    this.isExiting = true;
    setTimeout(() => {
      this.remove.emit(this.id);
    }, this.exitAnimationDurationMs);
  }


  private themeService = inject(ThemeService);

  getBgColor(): string {
    if (this.bg) {
      return ''; // Background color provided directly
    }
    return this.themeService.theme() === 'dark' ? 'bg-surface-2/50 dark:bg-slate-800/90' : 'bg-white/80 dark:bg-slate-800/50';
  }

  getBgStyle(): Record<string, string> {
    return this.bg ? { 'background-color': this.bg } : {};
  }

}
